import Vuex from "vuex";
import Vue from "vue";
import router from "@/router";
import Layout from "/src/layout/index.vue";
import { getNotificationList, getUserInfo, login, LogOut, mvpLogin, getUnseenDisclaimersCount } from "@/services/user";
import { companyRoute, getRoutes, requests } from "@/router/all-route";
import { highVarianceRoutes, pilotagePlanningRoutes } from "@/router/all-route";
import { pdMeterAuditLogRoutes } from "@/router/all-route";
import { removeToken } from "@/utils/auth";
const Ships = () => import("/src/view/admin/unqualified-ships.vue");
const Jetty = () => import("/src/view/admin/jetty.vue");
const Tank = () => import("/src/view/admin/tank.vue");
const Company = () => import("/src/view/admin/company.vue");
const Barging = () => import("/src/view/barging");
// Group related nav items into collapsible submenus for DISPLAY only.
// Runs after all role-based filtering, and only wraps when >=2 items survive,
// so routing (router.addRoutes uses the flat list) and per-role visibility are
// unchanged. Wrapped children carry `fullPath` so their menu link keeps the
// original URL (the sidebar falls back to fullPath when present).
function groupNavItems(routeList) {
  const buildGroup = (list, matchPaths, parentPath, meta) => {
    const matched = [];
    const rest = [];
    let insertAt = -1;
    list.forEach((item) => {
      const ok = item.component && item.children && item.children.length >= 1 && matchPaths.includes(item.path);
      if (ok) {
        if (insertAt === -1) insertAt = rest.length;
        matched.push(item);
      } else {
        rest.push(item);
      }
    });
    if (matched.length === 0) return list;
    const totalChildren = matched.reduce((n, it) => n + it.children.length, 0);
    if (totalChildren <= 1) {
      // Only one item survives for this role — leave it flat, don't wrap.
      rest.splice(insertAt, 0, ...matched);
      return rest;
    }
    const children = [];
    matched.forEach((it) => {
      it.children.forEach((ch) => children.push({ ...ch, fullPath: it.path + "/" + ch.path }));
    });
    rest.splice(insertAt, 0, {
      path: parentPath,
      component: Layout,
      meta: { ...meta, title: meta.key },
      children,
    });
    return rest;
  };

  let list = [...routeList];
  list = buildGroup(list, ["/certificate"], "/group-certificate", { key: "menu.groupCertificate", icon: "certificate-of-quality" });
  list = buildGroup(
    list,
    ["/equipment-report", "/excel", "/mooring-report", "/billing-record", "/bypass-seal-record", "/delay-report", "/inspector-documents", "/pdMeterAuditLog"],
    "/group-reports",
    { key: "menu.groupReports", icon: "menu-file" }
  );

  // Place the two groups directly below System Administration (/admin), in order
  // Certificate then Reports & Records. If /admin isn't shown for this role, leave them.
  const adminIdx = list.findIndex((i) => i.path === "/admin");
  if (adminIdx !== -1) {
    const order = ["/group-certificate", "/group-reports"];
    const groups = [];
    const rest = [];
    list.forEach((i) => (order.includes(i.path) ? groups.push(i) : rest.push(i)));
    if (groups.length) {
      groups.sort((a, b) => order.indexOf(a.path) - order.indexOf(b.path));
      const at = rest.findIndex((i) => i.path === "/admin") + 1;
      rest.splice(at, 0, ...groups);
      list = rest;
    }
  }
  return list;
}

Vue.use(Vuex);

const store = new Vuex.Store({
  state: {
    userInfo: null,
    routes: [],
    locale: "en",
    token: "",
    notifications: [],
    total: 0,
    screenType: {
      isMobileScreen: false,
      isMobileOrTabletScreen: false,
      isLaptop: false,
      mainLayoutWidth: 0
    },

    mvpVesselPayFlag: false,
    shipVettingCount: 0,
  },
  mutations: {
    SET_USERINFO(state, userInfo) {
      state.userInfo = { ...userInfo };
    },
    SET_CARDACTIVE(state, mvpVesselPayFlag) {
      state.mvpVesselPayFlag = mvpVesselPayFlag;
    },
    SET_ROUTES(state, routes) {
      state.routes = routes;
    },
    SET_TOKEN(state, token) {
      state.token = token;
    },
    changeLocale(state, value) {
      state.locale = value;
    },
    SET_NOTIFICATIONS(state, { notifications, total }) {
      if (state.notifications.length >= total) {
        return;
      }
      state.notifications = [...state.notifications, ...Array.from(notifications || [])];
      state.total = total;
    },
    CLEAR_NOTIFICATIONS(state) {
      state.notifications = [];
    },
    SET_SCREEN(state, value) {
      state.screenType = value;
    },
    SET_SHIP_VETTING_COUNT(state, count) {
      state.shipVettingCount = count;
    },
  },
  actions: {
    goLogin(commit, payLoad) {
      return new Promise((resolve, reject) => {
        let request = payLoad.isMvp ? mvpLogin(payLoad) : login(payLoad);
        request
          .then((res) => {
            if (res.code === 200) {
              resolve(res);
            } else {
              reject(res);
            }
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    clearNotificationList({ commit }) {
      commit("CLEAR_NOTIFICATIONS")
    },
    getNotificationList({ commit }, page = 1) {
      return new Promise((resolve, reject) => {
        getNotificationList(page)
          .then((res) => {
            if (!res) return;
            commit("SET_NOTIFICATIONS", { notifications: res.data.rows, total: res.data.total })
            resolve();
          })
          .catch((err) => reject(err));
      })
    },
    getUserInfo({ commit, dispatch }, isMvp) {
      return new Promise((resolve, reject) => {
        getUserInfo()
          .then((res) => {
            if (!res) return;
            let routeList = getRoutes(res.user.userType);
            let role = res.user.role;
            let userType = res.user.userType;
            let terminalLogisticRole = userType == 1 && role == 1
            let terminalPilotRole = userType == 1 && role == 10
            let terminalHeadPilotRole = userType == 1 && role == 12
            let terminalLoadingMaster = userType == 1 && role == 11
            let terminalOps = userType == 1 && role == 2 // Terminal Master Control Room
            let terminalLeader = userType == 1 && role == 3
            let terminalSuveyor = role == 6
            let terminalSuveyorAdmin = role == 7
            let terminalInpsector = role == 13
            let customer = role == 4
            let customerAdmin = role == 5
            let barge = role == 8
            let bargeOperator = role == 9
            // Loss Control (17) mirrors Terminal Logistics View (16) everywhere except the barging
            // nomination page, which has its own role-17 behaviour in the barging components.
            let logisticView = userType == 1 && (role == 16 || role == 17)

            // inspector can only view shipVetting
            if (terminalInpsector) {
              routeList = routeList.filter((item) => item.path == "/ship-vetting" || item.path == "/inspector-documents" || item.path == "*")
            }

            // company admin/customer user/barge/barge operator only can view certificate quantity,
            // except the Customer User (role 5) who can also view bill of lading.
            if (customer || customerAdmin || barge || bargeOperator) {
              routeList = routeList.filter(item => {
                if (item.path !== "/certificate" || !item.children) return true;
                if (item.children.some(child => child.path === "quality")) return false;
                if (item.children.some(child => child.path === "billOfLading") && role != 5) return false;
                return true;
              });
            }

            if (terminalSuveyor || terminalSuveyorAdmin) {
              routeList = routeList.filter(item =>
                item.path !== "/dashboard" &&
                item.path !== "/settings"
              );
              routeList = routeList.filter(item => {
                return !(item.path === "/certificate" && item.children &&
                  item.children.some(child => child.path === "quantity"));
              });
            }

            if (terminalLeader) {
              routeList = routeList.filter(item =>
                item.path !== "/dashboard" &&
                item.path !== "/admin" &&
                item.path !== "/settings"
              );
            }

            if (terminalOps) {
              routeList = routeList.filter(item =>
                item.path !== "/dashboard" &&
                item.path !== "/admin" &&
                item.path !== "/settings"
              );
            }

            if (terminalLoadingMaster) {
              routeList = routeList.filter(item =>
                item.path !== "/dashboard" &&
                item.path !== "/admin" &&
                item.path !== "/settings"
              );
            }

            if (terminalPilotRole || terminalHeadPilotRole) {
              routeList = routeList.filter(item =>
                item.path !== "/dashboard" &&
                item.path !== "/requests" &&
                item.path !== "/certificate" &&
                item.path !== "/inspector-documents" &&
                item.path !== "/settings"
              );
              if (terminalPilotRole) {
                routeList = routeList.filter(item =>
                  item.path !== "/admin"
                );
              } else {
                routeList.map((item, index) => {
                  if (item.path === "/admin") {
                    item.children = item.children.filter(e => e.path == "pilot")
                  }
                })
              }
            }

            // terminal only logistic role can view mooring report, audit log, billing record, stoppages
            if (userType == 1 && (role != 1 && role != 16 && role != 17)) {
              routeList = routeList.filter((item) => item.path !== "/mooring-report")
              routeList = routeList.filter((item) => item.path !== "/auditLog")
              routeList = routeList.filter((item) => item.path !== "/billing-record")
              routeList = routeList.filter((item) => item.path !== "/bypass-seal-record")
              routeList = routeList.filter((item) => item.path !== "/delay-report")
              routeList = routeList.filter((item) => item.path !== "/psa")
              routeList = routeList.filter((item) => item.path !== "/coa-link")
              routeList = routeList.filter((item) => item.path !== "/port-clear")

              // terminal only inspector and logistics can view ship-vetting
              if (role != 13) {
                routeList = routeList.filter((item) => item.path !== "/ship-vetting")
              }
            }

            // for non logistic role, must take out export data menu
            if (terminalLogisticRole || logisticView) {
              routeList.map((item, index) => {
                if (item.path === "/excel") {
                  item.children = item.children.filter(e => e.path == "logistic/index")
                }
              })
            } else if (terminalPilotRole || terminalHeadPilotRole) {
              routeList.map((item, index) => {
                if (item.path === "/excel") {
                  item.children = item.children.filter(e => e.path == "pilot/index")
                }
              })
            } else {
              routeList = routeList.filter((item) => item.path !== "/excel")
            }

            // for non head of pilot, must filter out the pilot management
            if (!terminalHeadPilotRole) {
              routeList.map((item, index) => {
                if (item.path === "/admin") {
                  item.children = item.children.filter(e => e.path != "pilot")
                }
              })
            }


            if (role == 1 || role == 2 || role == 16 || role == 17) {
              let target = routeList.find((item) => item.path === "/requests");
              if (!target) {
                routeList.push(requests);
              }
            }
            if (terminalLeader || userType === 0 || role == 1 || role == 16 || role == 17) {
              const alreadyIncluded = routeList.some(route => route.path === "/highVariance");
              if (!alreadyIncluded) {
                routeList.push(...highVarianceRoutes);
              }
            }
            if (role == 8 && userType == 4) {
              routeList = routeList.filter((item) => item.path !== "/admin")
            }

            // plilotage planning
            if (terminalOps || terminalLogisticRole || terminalHeadPilotRole || terminalInpsector || terminalLeader || terminalLoadingMaster || terminalPilotRole || logisticView) {
              const alreadyIncluded = routeList.some(route => route.path === "/pilotagePlanning");
              if (!alreadyIncluded) {
                routeList.push(...pilotagePlanningRoutes);
              }
            }

            // Loss Control (17) is a restricted subset of Terminal Management (16):
            // no mooring report, billing record, or pilotage planning, and no Company
            // Management under System Administration.
            if (userType == 1 && role == 17) {
              routeList = routeList.filter((item) =>
                item.path !== "/mooring-report" &&
                item.path !== "/billing-record" &&
                item.path !== "/pilotagePlanning"
              );
              routeList.forEach((item) => {
                if (item.path === "/admin" && item.children) {
                  item.children = item.children.filter((e) => e.path !== "company");
                }
              });
            }

            // PD Meter audit log: admin, logistics (1), logistics view (16), loss control (17)
            if (userType === 0 || terminalLogisticRole || logisticView) {
              const alreadyIncluded = routeList.some(route => route.path === "/pdMeterAuditLog");
              if (!alreadyIncluded) {
                routeList.push(...pdMeterAuditLogRoutes);
              }
            }

            // if (isMvp) {
            //   routeList = routeList.filter((item) => item.path !== "/admin")
            // }
            // // 客户公司管理员
            // if (userType == 2 && role == 4) {
            //   let target = routeList.find((item) => item.path === "/admin");
            //   if (!target) {
            //     routeList.splice(0, 0, companyRoute);
            //   }
            // }
            // 测量公司管理员
            // if (userType == 3 && role == 6) {
            //   let target = routeList.find((item) => item.path === "/admin");
            //   if (!target) {
            //     routeList.splice(0, 0, companyRoute);
            //   }
            // }
            router.addRoutes(routeList);
            commit("SET_USERINFO", res.user);
            commit("SET_CARDACTIVE", res.mvpVesselPayFlag)
            commit("SET_ROUTES", groupNavItems(routeList));
            resolve(routeList[0].redirect);
          })
          .catch((err) => reject(err));
      });
    },
    // 退出系统
    LogOut({ commit, state }) {
      return new Promise((resolve, reject) => {
        LogOut(state.token)
          .then(() => {
            commit("SET_USERINFO", {});
            removeToken();
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    setScreen({ commit }, data) {
      commit("SET_SCREEN", data);
    },
    async fetchShipVettingCount({ commit }) {
      try {
        // this is your service wrapper:
        const res = await getUnseenDisclaimersCount();
        // API returns { msg, code: 200, data: <number> }
        commit("SET_SHIP_VETTING_COUNT", res.data);
      }
      catch (err) {
        console.error("couldn't load unseen disclaimers", err);
      }
    }
  },
});
export default store;
